import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Lock, LogIn, ShoppingBag, Loader2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { api } from "../lib/api";
import { SKIN_STYLES } from "../lib/skins";

const SkinPreview = ({ id }) => {
  const st = SKIN_STYLES[id];
  return (
    <div
      className="relative h-24 w-full overflow-hidden rounded-2xl"
      style={{ background: st.bg, border: `2px solid ${st.c1}`, boxShadow: `0 0 24px ${st.glow}` }}
    >
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="font-display text-lg font-black"
          style={{
            background: `linear-gradient(90deg, ${st.c1}, ${st.c2})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          LÉGENDE
        </span>
        <span className="font-display text-xl font-black text-white/90">61 900</span>
      </div>
    </div>
  );
};

export const Store = ({ onBack }) => {
  const { t } = useLang();
  const { user, login } = useAuth();
  const [data, setData] = useState({ skins: [], owned: [] });
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .getSkins()
      .then(setData)
      .catch(() => setData({ skins: [], owned: [] }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load, user]);

  const handleBuy = async (lookup_key) => {
    setBuying(lookup_key);
    try {
      const res = await api.checkout(lookup_key, window.location.origin);
      window.location.href = res.checkout_url;
    } catch (e) {
      setBuying(null);
    }
  };

  return (
    <div className="flex min-h-full flex-col px-5 py-6" data-testid="store-screen">
      <button
        onClick={onBack}
        data-testid="store-back"
        className="mb-4 flex w-fit items-center gap-2 text-sm font-medium text-white/50 transition-colors hover:text-white"
      >
        <ArrowLeft size={18} /> {t("back")}
      </button>

      <h2 className="flex items-center gap-2 font-display text-3xl font-extrabold uppercase tracking-tight">
        <ShoppingBag className="text-[#f093fb]" /> {t("store")}
      </h2>
      <p className="mt-2 text-sm text-white/45">{t("storeSub")}</p>

      {!user && (
        <button
          onClick={login}
          data-testid="store-login-button"
          className="mt-5 flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 py-4 font-semibold text-white/90 transition-colors hover:bg-white/10"
        >
          <LogIn size={18} className="text-[#f093fb]" /> {t("loginGoogle")}
        </button>
      )}
      {!user && <p className="mt-2 text-center text-xs text-white/40">{t("loginToBuy")}</p>}

      {loading ? (
        <p className="animate-pulse-glow py-10 text-center text-white/50">{t("loading")}</p>
      ) : (
        <div className="mt-5 grid gap-4 pb-4 sm:grid-cols-2">
          {data.skins.map((s, i) => {
            const isOwned = data.owned.includes(s.lookup_key);
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                data-testid={`skin-card-${s.id}`}
                className="rounded-3xl border border-white/10 bg-white/[0.04] p-3"
              >
                <SkinPreview id={s.id} />
                <div className="mt-3 flex items-center justify-between px-1">
                  <div>
                    <p className="font-display font-bold text-white">{s.name}</p>
                    <p className="text-xs text-white/45">{s.price}</p>
                  </div>
                  {isOwned ? (
                    <span
                      data-testid={`skin-owned-${s.id}`}
                      className="flex items-center gap-1 rounded-full bg-[#38ef7d]/15 px-3 py-1.5 text-sm font-semibold text-[#38ef7d]"
                    >
                      <Check size={15} /> {t("owned")}
                    </span>
                  ) : (
                    <button
                      disabled={!user || buying === s.lookup_key}
                      onClick={() => handleBuy(s.lookup_key)}
                      data-testid={`skin-buy-${s.id}`}
                      className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#f093fb] to-[#f5576c] px-4 py-2 text-sm font-bold text-white transition-transform active:scale-95 disabled:opacity-40"
                    >
                      {buying === s.lookup_key ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : !user ? (
                        <Lock size={14} />
                      ) : null}
                      {t("buy")}
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};
